import React, { useContext, useEffect, useState } from "react";
import { useRouteLoaderData } from "react-router-dom";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import {
  getCallCompletion,
  getCallTime,
  getRepDistribution,
  getPhotoCapture,
  getEntities,
} from "../../API";

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatDuration(ms) {
  if (!ms || ms <= 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function completionColor(pct, threshold) {
  if (pct >= threshold) return "success";
  if (pct >= threshold * 0.8) return "warning";
  return "danger";
}

function distColor(pct) {
  if (pct >= 70) return "success";
  if (pct >= 40) return "warning";
  return "danger";
}

function SectionHeader({ title, expanded, onToggle }) {
  return (
    <div
      className="d-flex align-items-center gap-2 mb-2"
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={onToggle}
    >
      <span className="text-muted">{expanded ? "▾" : "▸"}</span>
      <h6 className="mb-0 fw-semibold">{title}</h6>
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div className="col">
      <div className="card shadow-sm text-center h-100">
        <div className="card-body py-3">
          <div className={`fs-4 fw-bold text-${color ?? "body"}`}>{value}</div>
          <div className="text-muted small">{label}</div>
        </div>
      </div>
    </div>
  );
}

async function fetchRepData({ repId, fromDate, toDate, manufacturerIds, threshold }) {
  const repIds = [Number(repId)];
  const [completion, callTime, distribution, photos] = await Promise.all([
    getCallCompletion({ fromDate, toDate, threshold, representativeIds: repIds, manufacturerIds }),
    getCallTime({ fromDate, toDate, flagThreshold: 0.2, representativeIds: repIds, manufacturerIds }),
    getRepDistribution({ fromDate, toDate, representativeIds: repIds, manufacturerIds }),
    getPhotoCapture({ fromDate, toDate, flagThreshold: 0.7, representativeIds: repIds, manufacturerIds }),
  ]);
  return { completion, callTime, distribution, photos };
}

export default function RepReport() {
  const [{ fromDate, toDate, manufacturers }] = useContext(ReportParamsContext);
  const auth = useRouteLoaderData("markit");
  const isRepUser = auth?.type === "REPRESENTATIVE";
  const lockedRepId = isRepUser ? auth?.representativeId : null;

  const [allReps, setAllReps] = useState([]);
  const [selectedRepId, setSelectedRepId] = useState(lockedRepId ? String(lockedRepId) : "");
  const [threshold, setThreshold] = useState(90);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [expanded, setExpanded] = useState({
    completion: true,
    callTime: true,
    photos: true,
    distribution: true,
  });

  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!isRepUser) {
      getEntities("representatives", "?size=1000").then((res) =>
        setAllReps(normalizeList(res).sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? "") || (a.firstName ?? "").localeCompare(b.firstName ?? "")))
      );
    }
  }, [isRepUser]);

  // Auto-generate when rep is locked and date range is ready
  useEffect(() => {
    if (!lockedRepId || !fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    setData(null);
    const manufacturerIds = manufacturers.map((m) => m.id);
    fetchRepData({ repId: lockedRepId, fromDate, toDate, manufacturerIds, threshold })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [lockedRepId, fromDate, toDate, threshold]); // manufacturers intentionally excluded: rep users re-run manually if needed

  const handleGenerate = async () => {
    const repId = lockedRepId ?? (selectedRepId ? Number(selectedRepId) : null);
    if (!repId) { setError("Please select a representative."); return; }
    if (!fromDate || !toDate) { setError("Please select at least one month in the parameters bar."); return; }

    setLoading(true);
    setError(null);
    setData(null);
    const manufacturerIds = manufacturers.map((m) => m.id);
    try {
      setData(await fetchRepData({ repId, fromDate, toDate, manufacturerIds, threshold }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const compRep   = data?.completion.reps[0]   ?? null;
  const timeRep   = data?.callTime.reps[0]      ?? null;
  const distRep   = data?.distribution.reps[0]  ?? null;
  const photoRep  = data?.photos.reps[0]        ?? null;

  const compPct   = compRep?.completionPct  ?? null;
  const avgMs     = timeRep?.avgCallMs      ?? null;
  const avgPhotos = photoRep?.avgPerCall    ?? null;
  const avgDist   = distRep?.overallAvgPct  ?? null;

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        {/* Parameters card */}
        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              {!isRepUser && (
                <div className="col-md-4">
                  <label className="form-label fw-medium mb-1">Representative</label>
                  <select
                    className="form-select"
                    value={selectedRepId}
                    onChange={(e) => setSelectedRepId(e.target.value)}
                  >
                    <option value="">— Select a representative —</option>
                    {allReps.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.lastName}, {r.firstName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {isRepUser && (
                <div className="col-md-4">
                  <label className="form-label fw-medium mb-1">Representative</label>
                  <div className="form-control-plaintext fw-medium">
                    {auth.firstname} {auth.lastname}
                  </div>
                </div>
              )}
              <div className="col-md-3">
                <label className="form-label fw-medium mb-1">Completion threshold (%)</label>
                <input
                  type="number"
                  className="form-control"
                  min={1} max={100}
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                />
              </div>
              <div className="col-md-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {data && (
          <>
            {/* KPI summary */}
            <div className="row row-cols-2 row-cols-md-4 g-3 mb-4">
              <KpiCard
                label="Call Completion"
                value={compPct != null ? `${compPct.toFixed(1)}%` : "—"}
                color={compPct != null ? completionColor(compPct, threshold) : "muted"}
              />
              <KpiCard
                label="Avg Time In Store"
                value={avgMs != null ? formatDuration(avgMs) : "—"}
              />
              <KpiCard
                label="Avg Photos / Call"
                value={avgPhotos != null ? avgPhotos.toFixed(1) : "—"}
              />
              <KpiCard
                label="Avg Distribution"
                value={avgDist != null ? `${avgDist.toFixed(1)}%` : "—"}
                color={avgDist != null ? distColor(avgDist) : "muted"}
              />
            </div>

            {/* Call Completion detail */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <SectionHeader
                  title="Call Completion"
                  expanded={expanded.completion}
                  onToggle={() => toggle("completion")}
                />
                {expanded.completion && (
                  compRep ? (
                    <>
                      <div className="text-muted small mb-2">
                        {compRep.totalActual} visits / {compRep.totalExpected.toFixed(1)} expected
                        &nbsp;·&nbsp; {data.completion.periodDays} day period
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-light">
                            <tr>
                              <th>Store</th>
                              <th>City</th>
                              <th className="text-end">Actual</th>
                              <th className="text-end">Expected</th>
                              <th>Completion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compRep.stores.map((s) => (
                              <tr key={s.storeId} className={s.flagged ? "table-warning" : ""}>
                                <td>
                                  {s.bannerName && <span className="badge bg-secondary me-1">{s.bannerName}</span>}
                                  {s.storeNumber && <span className="me-1">#{s.storeNumber}</span>}
                                  {s.storeAddress}
                                </td>
                                <td>{s.city}</td>
                                <td className="text-end">{s.actualVisits}</td>
                                <td className="text-end">{s.expectedVisits.toFixed(1)}</td>
                                <td>
                                  <span className={`badge bg-${completionColor(s.completionPct, threshold)}`}>
                                    {s.completionPct.toFixed(1)}%
                                  </span>
                                  <span className="text-muted ms-2" style={{ fontSize: "0.75rem" }}>
                                    ({s.visitFrequency}×/yr)
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted small mb-0">No data for this period.</p>
                  )
                )}
              </div>
            </div>

            {/* Store Call Time detail */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <SectionHeader
                  title="Store Call Time"
                  expanded={expanded.callTime}
                  onToggle={() => toggle("callTime")}
                />
                {expanded.callTime && (
                  timeRep ? (
                    <>
                      <div className="text-muted small mb-2">
                        {timeRep.visitCount} visits &nbsp;·&nbsp;
                        Total: {formatDuration(timeRep.totalMs)} &nbsp;·&nbsp;
                        Avg: {formatDuration(timeRep.avgCallMs)} &nbsp;·&nbsp;
                        Company avg: {formatDuration(data.callTime.companyAvgMs)}
                      </div>
                      {timeRep.manufacturers?.length > 0 && (
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-light">
                            <tr>
                              <th>Manufacturer</th>
                              <th className="text-end">Total time</th>
                              <th className="text-end">Avg / call</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timeRep.manufacturers.map((m) => (
                              <tr key={m.manufacturerId}>
                                <td>{m.manufacturerName || "(Unknown)"}</td>
                                <td className="text-end">{formatDuration(m.totalMs)}</td>
                                <td className="text-end">{formatDuration(m.avgMsPerCall)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  ) : (
                    <p className="text-muted small mb-0">No data for this period.</p>
                  )
                )}
              </div>
            </div>

            {/* Photo Capture detail */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <SectionHeader
                  title="Photo Capture"
                  expanded={expanded.photos}
                  onToggle={() => toggle("photos")}
                />
                {expanded.photos && (
                  photoRep ? (
                    <>
                      <div className="text-muted small mb-2">
                        {photoRep.totalPhotos} photos &nbsp;·&nbsp;
                        {photoRep.visitCount} visits &nbsp;·&nbsp;
                        Avg: {photoRep.avgPerCall.toFixed(2)} photos/call &nbsp;·&nbsp;
                        Company avg: {data.photos.companyAvg.toFixed(2)}
                      </div>
                      {photoRep.manufacturers?.length > 0 && (
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-light">
                            <tr>
                              <th>Manufacturer</th>
                              <th className="text-end">Photos</th>
                              <th className="text-end">Avg / call</th>
                            </tr>
                          </thead>
                          <tbody>
                            {photoRep.manufacturers.map((m) => (
                              <tr key={m.mfrGuid}>
                                <td>{m.mfrName}</td>
                                <td className="text-end">{m.photoCount}</td>
                                <td className="text-end">{m.avgPerCall.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  ) : (
                    <p className="text-muted small mb-0">No data for this period.</p>
                  )
                )}
              </div>
            </div>

            {/* Rep Distribution detail */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <SectionHeader
                  title="Distribution"
                  expanded={expanded.distribution}
                  onToggle={() => toggle("distribution")}
                />
                {expanded.distribution && (
                  distRep ? (
                    <>
                      <div className="text-muted small mb-2">
                        {distRep.totalStores} stores visited
                      </div>
                      {distRep.manufacturers?.map((mfr) => (
                        <div key={mfr.manufacturerId} className="mb-3">
                          <div className="fw-medium mb-1">
                            {mfr.manufacturerName}
                            <span className={`badge bg-${distColor(mfr.avgPct)} ms-2`}>
                              {mfr.avgPct.toFixed(1)}% avg
                            </span>
                          </div>
                          <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>UPC</th>
                                <th className="text-end">Stores YES</th>
                                <th className="text-end">Total Stores</th>
                                <th>Distribution</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mfr.products?.map((p) => (
                                <tr key={p.productId}>
                                  <td>{p.description}</td>
                                  <td className="text-muted">{p.upcCode}</td>
                                  <td className="text-end">{p.yesCount}</td>
                                  <td className="text-end">{p.totalStores}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className={`badge bg-${distColor(p.pct)}`}>
                                        {p.pct.toFixed(1)}%
                                      </span>
                                      <div className="progress flex-grow-1" style={{ height: 6 }}>
                                        <div
                                          className={`progress-bar bg-${distColor(p.pct)}`}
                                          style={{ width: `${Math.min(p.pct, 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-muted small mb-0">No data for this period.</p>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
